import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { components, operations } from "@/api/schema";

import { toListQuery, type OrderFilters } from "./use-orders";

export type AttentionCounts = components["schemas"]["AttentionCounts"];

type AttentionCountsQuery = NonNullable<
  operations["getOrderAttentionCounts"]["parameters"]["query"]
>;

/**
 * GET /v1/orders/attention-counts — total + per-reason counts of orders
 * needing review, scoped by the same filters as the orders list. Paging
 * (limit/offset) and `attention` itself are dropped: counts don't page, and
 * the BE endpoint has no `attention` param (it always counts across all four
 * reasons — see schema.d.ts `getOrderAttentionCounts`). Mirrors useOrders'
 * query-key/staleTime convention (api/use-orders.ts).
 */
export function useAttentionCounts(
  f: Omit<OrderFilters, "limit" | "offset">,
  options?: { enabled?: boolean },
) {
  // limit/offset/attention are dropped before building the query: the BE
  // endpoint has no params for them (see file doc comment above). The
  // parameter type already excludes limit/offset, but a caller can still
  // pass a full OrderFilters (e.g. the URL filter state) that carries them
  // at runtime — cast back to strip them for real, not just at the type level.
  const { limit: _l, offset: _o, attention: _a, ...rest } = f as OrderFilters;
  const query = toListQuery(rest) as AttentionCountsQuery;

  return useQuery({
    queryKey: ["orders", "attention-counts", query],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/orders/attention-counts", {
        params: { query },
      });
      if (error) throw error;
      return data;
    },
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}
